import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  ArrowLeft,
  ChevronRight,
  Clock,
  GraduationCap,
  FileText,
  CheckCircle2,
  RefreshCw,
  Mail,
  AlertCircle,
} from "lucide-react";

export default function BenefitProgramTutorialPage() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-slate-50 text-gray-900 font-sans pb-20">
      <header className="border-b bg-white sticky top-0 z-40 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex flex-col md:flex-row justify-between items-center gap-4">
          <Link to="/">
            <img
              src="/marketing/logo_official.png"
              className="h-28 object-contain"
              alt="OdontoAssist AI Logo"
            />
          </Link>

          <div className="flex items-center gap-6">
            <Link
              to="/ajuda/tutoriais"
              className="text-gray-500 hover:text-indigo-600 font-semibold hidden md:block"
            >
              Central de Guias
            </Link>

            <Link
              to="/"
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-2xl font-black tracking-wide flex items-center gap-2 transition-all shadow-lg hover:-translate-y-1"
            >
              <ArrowLeft size={20} />
              Voltar {user ? "para o Aplicativo" : "ao Início"}
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-10">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-500 mb-6">
          <Link to="/ajuda" className="hover:text-indigo-600">Ajuda</Link>
          <ChevronRight size={14} />
          <Link to="/ajuda/tutoriais" className="hover:text-indigo-600">Guias</Link>
          <ChevronRight size={14} />
          <span className="text-indigo-600">Gestão e Agenda</span>
        </div>

        <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-4 leading-tight">
          Programa de Benefícios OdontoAssist AI
        </h1>

        <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 mb-8">
          <div className="flex items-center gap-2">
            <Clock size={16} />
            <span>4 min de leitura</span>
          </div>
          <div className="flex items-center gap-2 bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full font-bold uppercase tracking-wider text-xs">
            Gestão e Agenda
          </div>
        </div>

        <article className="prose prose-indigo prose-lg max-w-none text-gray-600 text-justify">
          <p className="lead text-xl text-gray-700 font-medium">
            O Programa de Benefícios oferece um período gratuito de 90 dias para cirurgiões-dentistas que concluíram a graduação há até 12 meses. Todo o processo de solicitação, análise e acompanhamento é realizado diretamente pela plataforma.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
            Passo a Passo em Texto
          </h2>

          <div className="space-y-8 mt-8">
            <div className="bg-white p-8 rounded-3xl border shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />

              <h3 className="text-xl font-bold text-gray-900 mb-3">
                1. Acessando o Programa de Benefícios
              </h3>

              <p>
                Acesse a página <strong>Assinatura</strong> dentro do OdontoAssist AI.
              </p>

              <p>
                Nessa tela, você poderá visualizar a sua assinatura atual e, logo abaixo, a seção <strong>Programa de Benefícios</strong>.
              </p>

              <p>
                Essa seção mostra o status da solicitação, a validade do benefício, os dias restantes, o histórico das movimentações e, quando aplicável, a opção para solicitar o benefício.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl border shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />

              <h3 className="text-xl font-bold text-gray-900 mb-3">
                2. Solicitando o benefício
              </h3>

              <p>
                Clique em <strong>Solicitar benefício</strong> para abrir o formulário.
              </p>

              <p>
                O formulário é destinado exclusivamente a recém-formados. Informe os dados solicitados, como instituição de ensino e data de conclusão da graduação.
              </p>

              <p>
              O benefício é concedido uma única vez por usuário e possui validade de 90 dias após a aprovação.
              </p>              
            </div>

            <div className="bg-white p-8 rounded-3xl border shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />

              <h3 className="text-xl font-bold text-gray-900 mb-3">
                3. Enviando o comprovante
              </h3>

              <p>
                Para que a equipe possa analisar a solicitação, envie um comprovante atualizado.
              </p>

              <p>
                Podem ser utilizados documentos como:
              </p>

              <ul>
                <li>diploma;</li>
                <li>certificado de conclusão;</li>
                <li>declaração de conclusão;</li>
                <li>documento oficial emitido pela instituição;</li>
                <li>documento equivalente que comprove a conclusão da graduação há até 12 meses.</li>
              </ul>

              <p>
                Após o envio, a solicitação ficará com status <strong>Em análise</strong>.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl border shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />

              <h3 className="text-xl font-bold text-gray-900 mb-3">
                4. Acompanhando a análise
              </h3>

              <p>
                A equipe do OdontoAssist AI verificará os dados e o comprovante enviado.
              </p>

              <p>
                Enquanto a solicitação estiver em análise, o botão ficará bloqueado com a indicação <strong>Solicitação em análise</strong>.
              </p>

              <p>
                Quando a análise for concluída, você será notificado por e-mail e poderá acompanhar o resultado diretamente na página de Assinatura.
              </p>

              <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-5 mt-5">
                <h4 className="font-bold text-indigo-900 mb-3 flex items-center gap-2">
                  <Mail size={18} />
                  Notificações
                </h4>

                <p className="text-indigo-900">
                  O sistema pode enviar avisos automáticos por e-mail e, quando disponível, também por WhatsApp.
                </p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-3xl border shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />

              <h3 className="text-xl font-bold text-gray-900 mb-3">
                5. Utilizando o benefício aprovado
              </h3>

              <p>
                Após a aprovação, a plataforma exibirá:
              </p>

              <ul>
                <li>programa concedido;</li>
                <li>instituição informada;</li>
                <li>data de validade;</li>
                <li>quantidade de dias restantes;</li>
                <li>histórico do benefício.</li>
              </ul>

              <p>
                Durante o período aprovado, você poderá utilizar a plataforma conforme as regras do benefício concedido.
              </p>

              <p>
              Ao término dos 90 dias, o benefício é encerrado automaticamente. Caso deseje continuar utilizando a plataforma, basta ativar a assinatura principal.
              </p>              
            </div>

            <div className="bg-white p-8 rounded-3xl border shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />

              <h3 className="text-xl font-bold text-gray-900 mb-3">
                6. Quem pode participar
              </h3>

              <p>
                Atualmente, o Programa de Benefícios é destinado exclusivamente a:
              </p>

              <ul>
                <li>cirurgiões-dentistas formados há até 12 meses.</li>
              </ul>

              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 mt-5">
                <h4 className="font-bold text-blue-900 mb-3 flex items-center gap-2">
                  <CheckCircle2 size={18} />
                  Regra do benefício
                </h4>

                <p className="text-blue-900">
                  O benefício é concedido apenas uma vez por usuário, possui validade de 90 dias e não pode ser renovado.
                </p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-3xl border shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />

              <h3 className="text-xl font-bold text-gray-900 mb-3">
                7. Ativando a assinatura antecipadamente
              </h3>

              <p>
                Durante o benefício, não é necessário ativar a assinatura paga para continuar usando a plataforma dentro do prazo concedido.
              </p>

              <p>
                A ativação antecipada só é necessária se você quiser contratar módulos adicionais, como o <strong>Módulo WhatsApp Business</strong>, ou se desejar iniciar a assinatura principal antes do fim do benefício.
              </p>

              <p>
                Ao clicar em <strong>Ativar assinatura antecipadamente</strong>, o sistema exibirá uma confirmação antes de gerar a cobrança.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl border shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />

              <h3 className="text-xl font-bold text-gray-900 mb-3">
                8. Consultando o histórico
              </h3>

              <p>
                O histórico do benefício mostra as principais movimentações, como:
              </p>

              <ul>
                <li>solicitação enviada;</li>
                <li>aprovação;</li>
                <li>reprovação;</li>
                <li>lembretes automáticos;</li>
                <li>encerramento do benefício.</li>
              </ul>

              <p>
                Isso permite acompanhar todo o processo de forma transparente dentro da própria plataforma.
              </p>
            </div>
          </div>

          <div className="bg-indigo-900 rounded-3xl p-8 mt-12 text-white">
            <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <GraduationCap size={24} />
              Dicas importantes
            </h3>

            <ul className="text-indigo-100">
              <li>Envie sempre um comprovante recente e legível;</li>
              <li>Confira se a instituição e a data de conclusão foram preenchidas corretamente;</li>
              <li>O benefício possui validade de 90 dias e é concedido apenas uma vez;</li>
              <li>O programa é destinado exclusivamente a profissionais em início de carreira;</li>
              <li>Para contratar módulos adicionais, ative a assinatura principal.</li>
            </ul>

            <p className="mt-4">
              Em caso de dúvidas, entre em contato pelo WhatsApp (81) 3204-2969 ou pelo e-mail suporte@odontoassistai.com.br.
            </p>
          </div>
        </article>
      </main>
    </div>
  );
}
